import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  TrendingDown, 
  DollarSign, 
  Clock, 
  Activity, 
  Download, 
  FileText, 
  HelpCircle, 
  Info, 
  ChevronDown, 
  Lightbulb,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';

interface BillingAnalyticsData {
  totalSessions: number;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
  // New billing-based metrics
  totalHoursLost: number;
  revenuePreserved: number;
  revenueLost: number;
  agencyProfitLoss: number;
  lossOfProfitToAgency: number;
  stateBillingRate: number;
  caregiverRate: number;
  avgPatientCareHours: number;
  avgHospitalErHours: number;
}

interface BillingAnalyticsProps {
  agencyId: string | null;
  startDate?: Date;
  endDate?: Date;
  patientIds?: string[];
}

export default function BillingAnalytics({ 
  agencyId, 
  startDate: externalStartDate,
  endDate: externalEndDate,
  patientIds
}: BillingAnalyticsProps) {
  const chartColors = useChartColors();
  const [data, setData] = useState<BillingAnalyticsData>({
    totalSessions: 0,
    homeCases: 0,
    pcpCases: 0,
    urgentCareCases: 0,
    erCases: 0,
    totalHoursLost: 0,
    revenuePreserved: 0,
    revenueLost: 0,
    agencyProfitLoss: 0,
    lossOfProfitToAgency: 0,
    stateBillingRate: 0,
    caregiverRate: 0,
    avgPatientCareHours: 0,
    avgHospitalErHours: 0
  });
  const [loading, setLoading] = useState(true);
  const [hasBillingRates, setHasBillingRates] = useState(false);
  const [rateSource, setRateSource] = useState<'agency' | 'platform' | 'hardcoded'>('hardcoded');
  
  const startDate = externalStartDate ?? startOfMonth(new Date());
  const endDate = externalEndDate ?? endOfMonth(new Date());

  useEffect(() => {
    if (agencyId !== undefined || patientIds) {
      loadAnalyticsData();
    }
  }, [agencyId, startDate, endDate, patientIds]);

  // Default fallback rates - will be overridden by platform settings
  const HARDCODED_DEFAULT_STATE_RATE = 21.52;
  const HARDCODED_DEFAULT_CAREGIVER_RATE = 18.25;

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // First, load default rates from platform settings
      let defaultStateBillingRate = HARDCODED_DEFAULT_STATE_RATE;
      let defaultCaregiverRate = HARDCODED_DEFAULT_CAREGIVER_RATE;
      let platformSettingsLoaded = false;

      const { data: settingsData, error: settingsError } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'default_billing_rates')
        .maybeSingle();

      if (!settingsError && settingsData?.setting_value) {
        const settings = settingsData.setting_value as { state_billing_rate?: number; caregiver_rate?: number };
        defaultStateBillingRate = settings.state_billing_rate ?? HARDCODED_DEFAULT_STATE_RATE;
        defaultCaregiverRate = settings.caregiver_rate ?? HARDCODED_DEFAULT_CAREGIVER_RATE;
        platformSettingsLoaded = true;
      }

      // Get agency billing rates (or use defaults)
      let stateBillingRate = defaultStateBillingRate;
      let caregiverRate = defaultCaregiverRate;
      let usingAgencyRates = false;

      if (agencyId) {
        const { data: agencyData, error: agencyError } = await supabase
          .from('agencies')
          .select('state_billing_rate, caregiver_rate')
          .eq('id', agencyId)
          .single();

        if (!agencyError && agencyData) {
          // Check if agency has its own rates configured
          if (agencyData.state_billing_rate && agencyData.caregiver_rate) {
            stateBillingRate = agencyData.state_billing_rate;
            caregiverRate = agencyData.caregiver_rate;
            usingAgencyRates = true;
          } else {
            // Agency exists but doesn't have rates, use defaults
            stateBillingRate = agencyData.state_billing_rate || defaultStateBillingRate;
            caregiverRate = agencyData.caregiver_rate || defaultCaregiverRate;
          }
          setHasBillingRates(true);
        }
      } else {
        // For "All Agencies" view, use default rates from settings
        setHasBillingRates(true);
      }

      // Set the rate source
      if (usingAgencyRates) {
        setRateSource('agency');
      } else if (platformSettingsLoaded) {
        setRateSource('platform');
      } else {
        setRateSource('hardcoded');
      }

      // Get triage sessions with patient data for billing calculations
      let sessionsQuery = supabase
        .from('triage_sessions')
        .select(`
          id,
          recommendation,
          patient_id,
          patients!inner (
            patient_care_hours_weekly,
            hospital_er_hours
          )
        `)
        .not('completed_at', 'is', null);

      if (agencyId) {
        sessionsQuery = sessionsQuery.eq('agency_id', agencyId);
      }

      if (patientIds && patientIds.length > 0) {
        sessionsQuery = sessionsQuery.in('patient_id', patientIds);
      }

      if (startDate) {
        sessionsQuery = sessionsQuery.gte('completed_at', startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        sessionsQuery = sessionsQuery.lte('completed_at', endOfDay.toISOString());
      }

      const { data: sessions, error: sessionsError } = await sessionsQuery;

      if (sessionsError) throw sessionsError;

      // Calculate billing-based metrics
      let homeCases = 0;
      let pcpCases = 0;
      let urgentCareCases = 0;
      let erCases = 0;
      let totalHoursLost = 0;
      let totalPatientCareHours = 0;
      let totalHospitalErHours = 0;
      let patientCount = 0;

      (sessions || []).forEach((session: any) => {
        const patientCareHours = session.patients?.patient_care_hours_weekly || 40; // Default 40 hrs
        const hospitalErHours = session.patients?.hospital_er_hours || 8; // Default 8 hrs

        switch (session.recommendation) {
          case 'HOME':
            homeCases++;
            break;
          case 'PCP':
            pcpCases++;
            break;
          case 'URGENT_CARE':
            urgentCareCases++;
            break;
          case 'ER_NOW':
            erCases++;
            // Only ER visits cause billing hours to be lost
            totalHoursLost += hospitalErHours;
            break;
        }

        totalPatientCareHours += patientCareHours;
        totalHospitalErHours += hospitalErHours;
        patientCount++;
      });

      const totalSessions = homeCases + pcpCases + urgentCareCases + erCases;
      const divertedCases = homeCases + pcpCases + urgentCareCases;

      // Calculate averages
      const avgPatientCareHours = patientCount > 0 ? totalPatientCareHours / patientCount : 40;
      const avgHospitalErHours = patientCount > 0 ? totalHospitalErHours / patientCount : 8;

      // Revenue calculations based on billing rates
      // Hospital-ER Visit Cost = Hospital_ER hrs × State Billing Rate
      const revenueLostFromErVisits = totalHoursLost * stateBillingRate;

      // Revenue preserved = Diverted cases × avg hospital ER hours × state billing rate
      const revenuePreserved = divertedCases * avgHospitalErHours * stateBillingRate;

      // Agency profit/loss = Revenue impact - (hours × caregiver rate)
      // When billing is preserved, agency keeps the margin (state rate - caregiver rate)
      const marginPerHour = stateBillingRate - caregiverRate;
      const agencyProfitPreserved = divertedCases * avgHospitalErHours * marginPerHour;
      const agencyProfitLost = erCases * avgHospitalErHours * marginPerHour;
      const netAgencyProfitImpact = agencyProfitPreserved - agencyProfitLost;

      setData({
        totalSessions,
        homeCases,
        pcpCases,
        urgentCareCases,
        erCases,
        totalHoursLost,
        revenuePreserved,
        revenueLost: revenueLostFromErVisits,
        agencyProfitLoss: netAgencyProfitImpact,
        lossOfProfitToAgency: agencyProfitLost,
        stateBillingRate,
        caregiverRate,
        avgPatientCareHours,
        avgHospitalErHours
      });
    } catch (error) {
      console.error('Error loading billing analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    return hours.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' hrs';
  };

  const exportToCSV = () => {
    const dateRangeText = startDate && endDate 
      ? `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
      : 'All Time';

    const csvRows = [
      ['Billing-Based Analytics Report', ''],
      ['Generated Date', new Date().toLocaleDateString()],
      ['Report Period', dateRangeText],
      [''],
      ['Agency Billing Rates', ''],
      ['State Billing Rate', `$${data.stateBillingRate}/hr`],
      ['Caregiver Rate', `$${data.caregiverRate}/hr`],
      ['Margin per Hour', `$${(data.stateBillingRate - data.caregiverRate)}/hr`],
      [''],
      ['Triage Results', ''],
      ['Total Sessions', data.totalSessions.toString()],
      ['Home Care Cases', data.homeCases.toString()],
      ['PCP Cases', data.pcpCases.toString()],
      ['Urgent Care Cases', data.urgentCareCases.toString()],
      ['Hospital-ER Visit Cases', data.erCases.toString()],
      [''],
      ['Financial Impact', ''],
      ['Billing Hours Lost', formatHours(data.totalHoursLost)],
      ['Revenue Preserved', formatCurrency(data.revenuePreserved)],
      ['Hospital-ER Visit Cost', formatCurrency(data.revenueLost)],
      ['Loss of Profit to Agency', formatCurrency(data.lossOfProfitToAgency)],
      ['Net Profit Impact', formatCurrency(data.agencyProfitLoss)],
      [''],
      ['Patient Averages', ''],
      ['Avg Patient Care Hours/Week', formatHours(data.avgPatientCareHours)],
      ['Avg Hospital-ER Hours Lost', formatHours(data.avgHospitalErHours)],
    ];

    const csvContent = csvRows
      .map(row => row.map(cell => `\"${cell}\"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const startStr = startDate ? format(startDate, 'yyyy-MM-dd') : 'all';
    const endStr = endDate ? format(endDate, 'yyyy-MM-dd') : 'time';
    link.download = `billing-analytics-${startStr}-to-${endStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Billing analytics exported successfully');
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Billing Analytics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-16 bg-muted rounded"></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasBillingRates && agencyId) {
    return (
      <Card className="bg-white border-amber-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <span>Billing Analytics</span>
          </CardTitle>
          <CardDescription>
            Configure billing rates to view analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground mb-1">Billing rates not configured</p>
              <p>
                Please configure State Billing Rate and Caregiver Rate in the agency profile 
                to see billing-based analytics and financial impact calculations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const divertedCases = data.homeCases + data.pcpCases + data.urgentCareCases;
  const divertedPercentage = data.totalSessions > 0 
    ? ((divertedCases / data.totalSessions) * 100).toFixed(1) 
    : '0';

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-primary" />
              <span>Billing Analytics</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">
                      Calculates financial impact based on billing hours lost/preserved when patients 
                      visit ER/Hospital vs. receiving home care.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription className="flex items-center gap-2">
              Financial impact from preserved and lost billing hours
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] cursor-help ${
                        rateSource === 'agency' 
                          ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800' 
                          : rateSource === 'platform'
                          ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800'
                          : 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700'
                      }`}
                    >
                      {rateSource === 'agency' ? 'Agency Rates' : rateSource === 'platform' ? 'Platform Defaults' : 'System Defaults'}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">
                      {rateSource === 'agency' 
                        ? `Using this agency's configured billing rates: $${data.stateBillingRate}/hr state, $${data.caregiverRate}/hr caregiver.`
                        : rateSource === 'platform'
                        ? `Using platform default rates (no agency-specific rates configured): $${data.stateBillingRate}/hr state, $${data.caregiverRate}/hr caregiver.`
                        : `Using system fallback rates: $${data.stateBillingRate}/hr state, $${data.caregiverRate}/hr caregiver.`
                      }
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportToCSV}
            disabled={data.totalSessions === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Revenue Preserved */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 rounded-xl p-4 border border-green-200 dark:border-green-800 cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <Badge className="bg-green-100 text-green-800 text-xs">Preserved</Badge>
                  </div>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {formatCurrency(data.revenuePreserved)}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    Billing Revenue Preserved
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3">
                <p className="font-medium mb-1">Revenue Preserved Calculation:</p>
                <p className="text-sm text-muted-foreground">
                  {divertedCases} diverted cases × {data.avgHospitalErHours.toFixed(1)} avg ER hrs × ${data.stateBillingRate}/hr
                </p>
                <p className="text-sm text-green-600 font-medium mt-1">
                  = {formatCurrency(data.revenuePreserved)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Hospital-ER Visit Cost (Revenue Lost) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/50 dark:to-rose-950/50 rounded-xl p-4 border border-red-200 dark:border-red-800 cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                    <Badge className="bg-red-100 text-red-800 text-xs">Lost</Badge>
                  </div>
                  <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                    {formatCurrency(data.revenueLost)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                    Hospital-ER Visit Cost
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3">
                <p className="font-medium mb-1">Hospital-ER Visit Cost Calculation:</p>
                <p className="text-sm text-muted-foreground">
                  {formatHours(data.totalHoursLost)} lost × ${data.stateBillingRate}/hr (State Billing Rate)
                </p>
                <p className="text-sm text-red-600 font-medium mt-1">
                  = {formatCurrency(data.revenueLost)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Loss of Profit to Agency */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/50 dark:to-amber-950/50 rounded-xl p-4 border border-orange-200 dark:border-orange-800 cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingDown className="h-5 w-5 text-orange-600" />
                    <Badge className="bg-orange-100 text-orange-800 text-xs">Agency Loss</Badge>
                  </div>
                  <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                    {formatCurrency(data.lossOfProfitToAgency)}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                    Loss of Profit to Agency
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3">
                <p className="font-medium mb-1">Loss of Profit Calculation:</p>
                <p className="text-sm text-muted-foreground">
                  {data.erCases} Hospital-ER cases × {data.avgHospitalErHours.toFixed(1)} hrs × ${(data.stateBillingRate - data.caregiverRate).toFixed(2)}/hr margin
                </p>
                <p className="text-sm text-orange-600 font-medium mt-1">
                  = {formatCurrency(data.lossOfProfitToAgency)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Net Agency Profit Impact */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`bg-gradient-to-br ${data.agencyProfitLoss >= 0 
                  ? 'from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 border-blue-200 dark:border-blue-800' 
                  : 'from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 border-slate-200 dark:border-slate-800'
                } rounded-xl p-4 border cursor-help`}>
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className={`h-5 w-5 ${data.agencyProfitLoss >= 0 ? 'text-blue-600' : 'text-slate-600'}`} />
                    <Badge className={`${data.agencyProfitLoss >= 0 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-slate-100 text-slate-800'
                    } text-xs`}>
                      Net
                    </Badge>
                  </div>
                  <p className={`text-2xl font-bold ${data.agencyProfitLoss >= 0 
                    ? 'text-blue-700 dark:text-blue-400' 
                    : 'text-slate-700 dark:text-slate-400'
                  }`}>
                    {data.agencyProfitLoss >= 0 ? '+' : '-'}{formatCurrency(Math.abs(data.agencyProfitLoss))}
                  </p>
                  <p className={`text-xs mt-1 ${data.agencyProfitLoss >= 0 
                    ? 'text-blue-600 dark:text-blue-500' 
                    : 'text-slate-600 dark:text-slate-500'
                  }`}>
                    Net Profit Impact
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3">
                <p className="font-medium mb-1">Net Profit Impact:</p>
                <p className="text-sm text-muted-foreground">
                  Margin per hour: ${data.stateBillingRate}/hr - ${data.caregiverRate}/hr = ${(data.stateBillingRate - data.caregiverRate).toFixed(2)}/hr
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Profit preserved from {divertedCases} diverted cases minus losses from {data.erCases} Hospital-ER cases
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Hours Lost */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/50 dark:to-violet-950/50 rounded-xl p-4 border border-purple-200 dark:border-purple-800 cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <Badge className="bg-purple-100 text-purple-800 text-xs">Hours</Badge>
                  </div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                    {formatHours(data.totalHoursLost)}
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-500 mt-1">
                    Billing Hours Lost
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3">
                <p className="font-medium mb-1">Hours Lost Calculation:</p>
                <p className="text-sm text-muted-foreground">
                  {data.erCases} Hospital-ER cases × avg {data.avgHospitalErHours.toFixed(1)} hrs per visit
                </p>
                <p className="text-sm text-purple-600 font-medium mt-1">
                  = {formatHours(data.totalHoursLost)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Case Breakdown */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-muted-foreground">Home Care</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{data.homeCases}</p>
            <p className="text-xs text-green-600">Billing preserved</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-muted-foreground">PCP Visits</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{data.pcpCases}</p>
            <p className="text-xs text-green-600">Billing preserved</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-muted-foreground">Urgent Care</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{data.urgentCareCases}</p>
            <p className="text-xs text-green-600">Billing preserved</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-red-600" />
              <span className="text-sm text-muted-foreground">Hospital-ER Visit</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{data.erCases}</p>
            <p className="text-xs text-red-600">Billing stopped</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Diverted from Hospital</p>
            <p className="text-xl font-bold text-green-600">{divertedCases} cases</p>
            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mt-1">
              {divertedPercentage}% diversion rate
            </Badge>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Avg Care Hrs/Week</p>
            <p className="text-xl font-bold text-foreground">{data.avgPatientCareHours.toFixed(1)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Avg ER Hrs Lost</p>
            <p className="text-xl font-bold text-foreground">{data.avgHospitalErHours.toFixed(1)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Total Sessions</p>
            <p className="text-xl font-bold text-foreground">{data.totalSessions}</p>
          </div>
        </div>

        {/* Billing Rates Info */}
        <div className="bg-muted/50 rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Agency Billing Configuration</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="flex flex-col">
              <span className="text-muted-foreground">State Billing Rate</span>
              <span className="font-semibold text-primary">${data.stateBillingRate}/hr</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Caregiver Rate</span>
              <span className="font-semibold text-blue-600">${data.caregiverRate}/hr</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Profit Margin</span>
              <span className="font-semibold text-green-600">
                ${(data.stateBillingRate - data.caregiverRate).toFixed(2)}/hr
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Avg ER Hours</span>
              <span className="font-semibold text-foreground">{data.avgHospitalErHours.toFixed(1)} hrs</span>
            </div>
          </div>
        </div>

        {/* What Does This Mean? */}
        <Collapsible className="mt-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-4 h-auto bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-lg group">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                <span className="font-medium text-foreground">Understanding Billing Analytics</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="bg-card border border-border rounded-lg p-5 space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">How Billing Works in Home Care</h4>
                <p className="text-sm text-muted-foreground">
                  Home care agencies bill State Governments (Medicaid/Medicare) based on hours of care provided. 
                  When a patient is hospitalized or visits the ER, billing <span className="font-medium text-red-600">stops</span> until they are discharged.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-foreground mb-2">Hospital-ER Visit Cost</h4>
                <p className="text-sm text-muted-foreground">
                  The cost is calculated as: <span className="font-medium text-foreground">Hospital/ER Hours × State Billing Rate</span>.
                  This represents the revenue lost when billing stops due to hospitalization.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Agency Profit Impact</h4>
                <p className="text-sm text-muted-foreground">
                  Agencies earn the difference between State Billing Rate and Caregiver Rate. When patients avoid unnecessary 
                  ER visits, agencies preserve this profit margin. The calculation shows net impact from both preserved and lost cases.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  Example
                </h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Patient with 40 hrs/week care, ER visit causes 8 hours of lost billing.
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  At ${data.stateBillingRate}/hr state rate: <span className="text-red-600 font-medium">${(8 * data.stateBillingRate).toFixed(0)} revenue lost</span>
                </p>
                <p className="text-sm font-medium text-green-600">
                  Preventing that ER visit preserves ${(8 * (data.stateBillingRate - data.caregiverRate)).toFixed(0)} in agency profit.
                </p>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
